import { useParams, Link, useNavigate } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const comic = comics.find(c => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <SEOHead title="漫画未找到 - 八仔漫画网" description="您要查看的漫画不存在或已被删除。" />
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
              <i className="ri-file-unknow-line text-foreground-400 text-3xl"></i>
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">漫画未找到</h2>
            <p className="text-foreground-500 text-sm mb-6">您要查看的漫画不存在或已被删除</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter(c => c.id !== comic.id && c.categories.some(cat => comic.categories.includes(cat)))
    .slice(0, 6);

  const primaryCategory = comic.categories[0];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 在线免费阅读 - 八仔漫画网`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.createdAt,
    dateModified: comic.updatedAt,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: '八仔漫画网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://bazaimanhua.com/comic/${comic.id}`,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://bazaimanhua.com' },
      { '@type': 'ListItem', position: 2, name: `${primaryCategory}漫画`, item: `https://bazaimanhua.com/category/${primaryCategory}` },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  const formatViews = (views: number): string => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  const formatDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 在线免费阅读 - 八仔漫画网`}
        description={comic.description}
        keywords={`${comic.title},在线免费阅读,八仔漫画网,${comic.categories.join(',')}漫画`}
        canonicalUrl={`https://bazaimanhua.com/comic/${comic.id}`}
        ogType="article"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: `${primaryCategory}漫画`, to: `/category/${primaryCategory}` },
                { label: comic.title },
              ]}
            />
          </div>
        </div>

        <article className="max-w-7xl mx-auto px-4 md:px-6 py-8" itemScope itemType="https://schema.org/Article">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-shrink-0 w-full lg:w-[300px]">
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={comic.cover}
                  alt={`${comic.title}漫画封面`}
                  title={`${comic.title} - ${comic.author}作品`}
                  className="w-full h-full object-cover object-top"
                  width={300}
                  height={420}
                  itemProp="image"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3" itemProp="headline">
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm text-foreground-600" itemProp="author">
                  <i className="ri-user-line mr-1"></i>{comic.author}
                </span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  comic.status === '已完结'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-primary-100 text-primary-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-4 mb-6">
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  <span className="text-sm font-bold text-foreground-900">{comic.rating}</span>
                  <span className="text-xs text-foreground-500">分</span>
                </div>
                <div className="flex items-center gap-1 text-sm text-foreground-600">
                  <i className="ri-eye-line"></i>
                  <span>{formatViews(comic.views)} 次阅读</span>
                </div>
                <div className="flex items-center gap-1 text-sm text-foreground-600">
                  <i className="ri-calendar-line"></i>
                  <span>更新于 {formatDate(comic.updatedAt)}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-6">
                {comic.categories.map((cat) => (
                  <Link
                    key={cat}
                    to={`/category/${cat}`}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    {cat}
                  </Link>
                ))}
              </div>

              <div className="bg-background-100 rounded-lg p-4 md:p-5 mb-6" itemProp="description">
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">作品简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/comic/${comic.id}/read/1`}
                  className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  开始阅读 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
                <Link
                  to={`/comic/${comic.id}/read/${comic.chapters[comic.chapters.length - 1].number}`}
                  className="px-8 py-3 bg-background-50 border border-background-300 text-foreground-700 rounded-full font-medium text-sm hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  阅读最新话
                </Link>
              </div>
            </div>
          </div>

          <section className="mt-12" aria-label="章节列表">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900">
                章节列表 <span className="text-sm text-foreground-500 font-normal ml-2">共 {comic.chapters.length} 话</span>
              </h2>
              <button className="text-xs text-foreground-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer">
                <i className="ri-sort-desc"></i>
                倒序
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {comic.chapters.map((chapter) => (
                <Link
                  key={chapter.id}
                  to={`/comic/${comic.id}/read/${chapter.number}`}
                  className="flex items-center justify-between px-4 py-3 bg-background-50 border border-background-200 rounded-lg hover:border-primary-300 hover:bg-background-50 transition-colors cursor-pointer group"
                >
                  <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors truncate mr-2">
                    {chapter.title}
                  </span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap flex-shrink-0">
                    {formatDate(chapter.updatedAt)}
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {relatedComics.length > 0 && (
            <section className="mt-14" aria-label="相关推荐">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900">相关推荐</h2>
                  <p className="text-sm text-foreground-500 mt-1">你可能也会喜欢这些漫画</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}